import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MapContainer, GeoJSON, Polygon, Tooltip, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { X, ChevronRight, ChevronLeft, Map as MapIcon, Info, ArrowLeft, LayoutGrid } from "lucide-react";
import worldCountriesGeoJson from "@/lib/worldCountries.json";
import worldSubdivisionsGeoJson from "@/lib/worldSubdivisions.json";
import { aliasAdmin1 } from "@/lib/wineRegionAliases";
import useIsMobile from "@/hooks/useIsMobile";

/* Drill-down levels:
 *  0 = country picker  (Italia + foreign countries with wines)
 *  1 = regions of Italia OR foreign zones of a country (all served by /api/map/zones)
 *  2 = provinces of an Italian region (from /api/map/zones, zone_type=provincia)
 *
 * Bottom-sheet (indipendente dai livelli mappa):
 *   - Chiuso finché l'utente non seleziona una zona (regione o provincia).
 *   - Si apre a snap `half` sui produttori raggruppati. La mappa resta
 *     visibile e navigabile sopra il pannello.
 *   - Snap points: peek(20vh) / half(55vh) / full(92vh) con drag verticale
 *     sull'handle in cima. Swipe-down oltre peek → chiude.
 *   - Click produttore: entra nella sua lista vini (stesso pannello, no flicker).
 *   - Breadcrumb "Regione › Provincia › Produttore" con back-per-segmento.
 *   - Cambiando zona sulla mappa il pannello si aggiorna senza chiudersi.
 */

const activeColor = "#8B0033";
const activeStroke = "#5C0F1F";
const inactiveColor = "#B8B0A5";
const denomStroke = "#8B0033";

const SNAP = { peek: 0.20, half: 0.55, full: 0.92 };

/* Fase 6.8 pt.3: su mobile la mappa è quasi statica per non ostacolare
 * la selezione (no drag/pan, no scroll-zoom, no zoom control).
 * Fit-bounds automatico ad ogni livello via `key` react.
 * Fase 7.2: hook estratto in @/hooks/useIsMobile (condiviso con Header). */

/**
 * MapStaticEnforcer — child del MapContainer.
 * Fase 6.8 pt.3 (fix): disabilita programmaticamente OGNI pointer-based
 * pan/zoom sulla mappa Leaflet quando il viewport è mobile. Il fatto di
 * disabilitare le props del MapContainer non basta: alcuni handler
 * (dragging, tap) sono registrati direttamente dall'istanza Leaflet
 * indipendentemente dalle props, e ignorano il tipo di pointer (touch
 * vs mouse). Chiamare `map.dragging.disable()` rimuove il listener a
 * livello di istanza per QUALSIASI pointer.
 */
function MapStaticEnforcer({ isMobile }) {
    const map = useMap();
    useEffect(() => {
        if (!map) return;
        if (isMobile) {
            map.dragging.disable();
            map.touchZoom.disable();
            map.doubleClickZoom.disable();
            map.scrollWheelZoom.disable();
            map.boxZoom.disable();
            map.keyboard.disable();
            if (map.tap) map.tap.disable();
            if (map.zoomControl) map.zoomControl.remove();
            // Blocca anche il pan via CSS: elimina inerzia e touch-panning.
            const c = map.getContainer();
            if (c) {
                c.style.touchAction = "manipulation";
                c.style.cursor = "default";
            }
        } else {
            map.dragging.enable();
            map.touchZoom.enable();
            map.doubleClickZoom.enable();
            map.scrollWheelZoom.enable();
            map.boxZoom.enable();
            map.keyboard.enable();
            if (map.tap) map.tap.enable();
        }
    }, [map, isMobile]);
    return null;
}

/* Fase 7.3: componente che riceve un GeoJSON e chiama `map.fitBounds` sui suoi
   confini reali. Robusto anche con feature singole o geometrie complesse. */
function FitBoundsToGeoJson({ data, deps = [], padding = [20, 20] }) {
    const map = useMap();
    useEffect(() => {
        if (!map || !data) return;
        // eslint-disable-next-line global-require
        const L = require("leaflet");
        try {
            const layer = L.geoJSON(data);
            const bounds = layer.getBounds();
            if (bounds && bounds.isValid()) {
                map.fitBounds(bounds, { padding, animate: true, duration: 0.6 });
            }
        } catch {}
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, ...deps]);
    return null;
}

/**
 * Fase 8.4 · MapAutoInvalidate — child del MapContainer.
 *
 * Fix classico Leaflet: se il container era nascosto/con size diversa al
 * mount (es. toggle Griglia↔Mappa) la mappa mantiene le dimensioni
 * sbagliate e mostra "l'Italia piccola in un riquadro" col resto del
 * canvas vuoto.
 *
 * Interventi:
 *   1. `invalidateSize()` in double rAF post-mount → aspetta layout stabile.
 *   2. `ResizeObserver` sul container: qualsiasi cambio dimensione ricalcola.
 *   3. Su visibilità (IntersectionObserver / display: none → visible) rilancia.
 */
function MapAutoInvalidate() {
    const map = useMap();
    useEffect(() => {
        if (!map) return;
        const container = map.getContainer();
        if (!container) return;
        let cancelled = false;
        const invalidate = () => {
            if (cancelled || !map || !map.getContainer()) return;
            try { map.invalidateSize({ animate: false, pan: false }); } catch {}
        };
        // Post-mount: due rAF garantiscono che React abbia già applicato
        // il layout e Leaflet non veda l'iniziale w=0/h=0.
        const raf1 = requestAnimationFrame(() => {
            const raf2 = requestAnimationFrame(invalidate);
            invalidate.__raf2 = raf2;
        });
        // ResizeObserver: cattura ogni resize successivo (rotazione device,
        // toggle sidebar, splitter, ecc.).
        let ro = null;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(() => invalidate());
            ro.observe(container);
        }
        // Fallback: window resize (per browser vecchi).
        window.addEventListener("resize", invalidate, { passive: true });
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf1);
            if (invalidate.__raf2) cancelAnimationFrame(invalidate.__raf2);
            if (ro) ro.disconnect();
            window.removeEventListener("resize", invalidate);
        };
    }, [map]);
    return null;
}

/**
 * Fase 8.4 · Filtra le zone che NON devono essere disegnate sulla mappa.
 *
 * Le "country zones" create dall'autofill con COUNTRY_BBOX (`source="bundled"`
 * e `region == country`) sono rettangoli — servono solo per il matching
 * interno lato server (fallback quando l'AI non riesce a proporre una
 * regione). Se disegnate producono il "riquadro bordeaux attorno all'Italia"
 * segnalato dal cliente.
 *
 * Regola: escludi qualunque zona il cui poligono sia esattamente un
 * bounding-box paese (source=bundled + region uguale al country + zone_type
 * regione). Non tocchiamo le geometrie reali (source=ai_generated,
 * source=manual, source=natural_earth).
 */
function isRenderableZone(z) {
    if (!z) return false;
    const region = (z.region || "").trim().toLowerCase();
    const country = (z.country || "").trim().toLowerCase();
    const src = (z.source || "").toLowerCase();
    // country-level bbox: source=bundled + region == country
    if (src === "bundled" && region === country && region.length > 0) return false;
    // Zone auto-country (nomi possibili alternativi) e zone senza geometria reale
    if (src === "bbox" || src === "auto_country" || src === "country_bbox") return false;
    // Zona con render=false esplicito (contratto opzionale se aggiunto lato server)
    if (z.render === false) return false;
    return true;
}

/* Fase 7.5: sezione produttore con vini auto-expanded.
 * - `eagerLoad`: primi N produttori caricano immediatamente all'entrata nella
 *   griglia. Gli altri usano IntersectionObserver per fetch on-demand solo
 *   quando entrano nel viewport → performance con 50+ produttori.
 * - Cache locale per evitare refetch se l'utente scrolla su/giù. */
function ProducerSection({ producer, scope, eagerLoad, onOpenWine, onOpenProducer }) {
    const [wines, setWines] = useState(null);
    const [loading, setLoading] = useState(false);
    const sectionRef = useRef(null);
    const loadedRef = useRef(false);

    const load = useCallback(async () => {
        if (loadedRef.current) return;
        loadedRef.current = true;
        setLoading(true);
        const params = new URLSearchParams({
            produttore: producer.producer,
            limit: "50",
        });
        if (scope.provincia) params.set("provincia", scope.provincia);
        else if (scope.region) params.set("regione", scope.region);
        if (scope.country) params.set("paese", scope.country);
        try {
            const { data } = await api.get(`/wines?${params}`);
            setWines(data.items || []);
        } catch {
            setWines([]);
        } finally {
            setLoading(false);
        }
    }, [producer.producer, scope.country, scope.region, scope.provincia]);

    useEffect(() => {
        if (eagerLoad) { load(); return; }
        if (!sectionRef.current) return;
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting) { load(); io.disconnect(); break; }
                }
            },
            { rootMargin: "300px 0px" }
        );
        io.observe(sectionRef.current);
        return () => io.disconnect();
    }, [eagerLoad, load]);

    return (
        <section
            ref={sectionRef}
            className="border-t border-[hsl(var(--border))] pt-4"
            data-testid={`grid-producer-${producer.producer}`}
        >
            <div className="flex items-baseline justify-between gap-3 mb-3">
                <button
                    onClick={onOpenProducer}
                    className="text-left group flex-1 min-w-0"
                    data-testid={`grid-producer-heading-${producer.producer}`}
                    title="Apri scheda dedicata al produttore"
                >
                    <h3 className="text-lg sm:text-xl leading-tight group-hover:text-[hsl(var(--primary))]">
                        {producer.producer}
                    </h3>
                    <p className="co-caption mt-0.5">{producer.wine_count} vin{producer.wine_count === 1 ? "o" : "i"}</p>
                </button>
            </div>
            {wines === null && !loading && (
                <div className="h-24 rounded-sm bg-[hsl(var(--secondary))]/50 animate-pulse" aria-hidden />
            )}
            {loading && (
                <p className="text-xs text-[hsl(var(--muted-foreground))] py-2">Caricamento vini…</p>
            )}
            {Array.isArray(wines) && wines.length === 0 && !loading && (
                <p className="text-xs text-[hsl(var(--muted-foreground))] py-2 italic">Nessun vino nel filtro corrente.</p>
            )}
            {Array.isArray(wines) && wines.length > 0 && (
                <ul className="divide-y divide-[hsl(var(--border))] border border-[hsl(var(--border))] rounded-sm bg-white">
                    {wines.map((w) => (
                        <li key={w.id}>
                            <button
                                onClick={() => onOpenWine(w.id)}
                                className="w-full flex items-baseline gap-3 px-4 py-2.5 text-left hover:bg-[hsl(var(--secondary))]/40 transition-colors group"
                                data-testid={`grid-wine-${w.id}`}
                            >
                                <span className="text-[10px] uppercase tracking-wide text-[hsl(var(--muted-foreground))] shrink-0 w-16">
                                    {w.type || "vino"}
                                </span>
                                <span className="flex-1 min-w-0 text-sm group-hover:text-[hsl(var(--primary))] truncate">
                                    {w.name}
                                    {w.vintage && <span className="ml-1.5 italic text-[hsl(var(--muted-foreground))]">{w.vintage}</span>}
                                </span>
                                <span className="hidden sm:inline text-xs text-[hsl(var(--muted-foreground))] truncate max-w-[180px]">
                                    {w.denomination || ""}
                                </span>
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </section>
    );
}


function zonePolygonPositions(zone) {
    if (Array.isArray(zone.polygon)) return zone.polygon;
    return null;
}

function polygonToGeoJSON(zone) {
    const positions = zonePolygonPositions(zone);
    if (!positions) return null;
    const coords = positions.map(([lat, lon]) => [lon, lat]);
    if (coords.length && (coords[0][0] !== coords[coords.length - 1][0] ||
                            coords[0][1] !== coords[coords.length - 1][1])) {
        coords.push(coords[0]);
    }
    return {
        type: "Feature",
        properties: { name: zone.name },
        geometry: { type: "Polygon", coordinates: [coords] },
    };
}

function zonesToFeatureCollection(zones) {
    const features = [];
    for (const z of zones) {
        if (z.geojson) {
            features.push({
                type: "Feature",
                properties: { name: z.name, region: z.region, id: z.id, count: z.wine_count || 0 },
                geometry: z.geojson,
            });
        } else if (Array.isArray(z.polygon)) {
            const f = polygonToGeoJSON(z);
            if (f) {
                f.properties = { name: z.name, region: z.region, id: z.id, count: z.wine_count || 0 };
                features.push(f);
            }
        }
    }
    return { type: "FeatureCollection", features };
}

export default function MapExplore() {
    const nav = useNavigate();
    const isMobile = useIsMobile();
    const [level, setLevel] = useState(0);
    const [country, setCountry] = useState(null);
    const [region, setRegion] = useState(null);
    const [provincia, setProvincia] = useState(null);
    const [zones, setZones] = useState([]);
    const [provinceZones, setProvinceZones] = useState([]);
    const [denominazioni, setDenominazioni] = useState([]);
    const [denomEnabled, setDenomEnabled] = useState(false);
    // Fase 7.2: toggle Griglia | Mappa al livello 0. Persist localStorage.
    const [viewMode, setViewMode] = useState(() => {
        try { return localStorage.getItem("co_map_view") || "grid"; } catch { return "grid"; }
    });
    useEffect(() => {
        try { localStorage.setItem("co_map_view", viewMode); } catch {}
    }, [viewMode]);
    // Fase 7.4: drill-down griglia — stato indipendente dal flow mappa.
    // Step: 0=paesi · 1=regioni · 2=province (solo IT) · 3=produttori · 4=vini.
    const [gridStep, setGridStep] = useState(0);
    const [gridCountry, setGridCountry] = useState(null);
    const [gridRegion, setGridRegion] = useState(null);
    const [gridProvincia, setGridProvincia] = useState(null);
    const [gridProducer, setGridProducer] = useState(null);

    // Bottom sheet state
    const [sheetOpen, setSheetOpen] = useState(false);
    const [sheetSnap, setSheetSnap] = useState("half"); // peek | half | full
    const [producers, setProducers] = useState([]);
    const [loadingProducers, setLoadingProducers] = useState(false);
    const [selectedProducer, setSelectedProducer] = useState(null);
    const [producerWines, setProducerWines] = useState([]);
    const [loadingWines, setLoadingWines] = useState(false);

    // Fase 7.4: fetch dell'aggregato reale wines→region/provincia via API.
    // Serve per popolare la griglia province italiane con conteggi reali,
    // anche quando `map_zones` non ha `parent_region` valorizzato per la
    // regione target (es. Toscana). Fonte di verità = dati vini in Mongo.
    const [regionsMeta, setRegionsMeta] = useState([]);
    useEffect(() => {
        api.get("/map/zones", { params: { zone_type: "regione" } })
            .then(({ data }) => setZones(data.items || []))
            .catch(() => setZones([]));
        api.get("/map/zones", { params: { zone_type: "provincia" } })
            .then(({ data }) => setProvinceZones(data.items || []))
            .catch(() => setProvinceZones([]));
        api.get("/map/zones", { params: { zone_type: "denominazione" } })
            .then(({ data }) => {
                setDenominazioni(data.items || []);
                setDenomEnabled(!!data.denominazione_enabled);
            })
            .catch(() => {});
        api.get("/wines/regions")
            .then(({ data }) => setRegionsMeta(data.items || []))
            .catch(() => setRegionsMeta([]));
    }, []);

    const countriesWithWines = useMemo(() => {
        const m = {};
        for (const z of zones) {
            const c = (z.country || "").trim();
            if (!c) continue;
            m[c] = (m[c] || 0) + (z.wine_count || 0);
        }
        return Object.entries(m).map(([c, n]) => ({ country: c, count: n }))
            .sort((a, b) => b.count - a.count);
    }, [zones]);

    // `currentCountryZones` è la lista completa (usata per matching interno,
    // p.es. lookup admin1 → wine_count). Il rendering della mappa usa
    // `renderableCountryZones` (Fase 8.4) che esclude le zone bbox-paese per
    // evitare il "riquadro bordeaux attorno all'Italia".
    const currentCountryZones = useMemo(() => {
        return zones.filter((z) => (z.country || "").toLowerCase() === (country || "").toLowerCase());
    }, [zones, country]);

    const renderableCountryZones = useMemo(() => {
        return currentCountryZones.filter(isRenderableZone);
    }, [currentCountryZones]);

    const currentRegionProvinces = useMemo(() => {
        if (!region) return [];
        return provinceZones.filter((z) =>
            (z.country || "").toLowerCase() === "italia" &&
            (z.parent_region || "").toLowerCase() === region.toLowerCase()
        );
    }, [provinceZones, region]);

    const currentDenominazioni = useMemo(() => {
        if (!denomEnabled) return [];
        return denominazioni.filter((d) => {
            const c = (d.country || "").toLowerCase();
            const rp = (d.parent_region || "").toLowerCase();
            if (region) return c === (country || "").toLowerCase() && rp === region.toLowerCase();
            if (country) return c === (country || "").toLowerCase();
            return false;
        });
    }, [denominazioni, denomEnabled, country, region]);

    // Fetch producers whenever region/provincia (or country only) change and sheet is open.
    // Il pannello NON viene mai smontato — il contenuto muta in-place senza flicker.
    const fetchProducers = useCallback(async (scope) => {
        setLoadingProducers(true);
        setSelectedProducer(null);
        setProducerWines([]);
        try {
            const p = new URLSearchParams();
            if (scope.country) p.set("country", scope.country);
            if (scope.region) p.set("region", scope.region);
            if (scope.provincia) p.set("provincia", scope.provincia);
            const { data } = await api.get(`/wines/producers?${p}`);
            setProducers(data.items || []);
        } catch {
            setProducers([]);
        } finally {
            setLoadingProducers(false);
        }
    }, []);

    const fetchProducerWines = async (producerName) => {
        setSelectedProducer(producerName);
        setLoadingWines(true);
        setProducerWines([]);
        const p = new URLSearchParams({ produttore: producerName, limit: "50" });
        if (region) p.set("regione", region);
        if (country) p.set("paese", country);
        try {
            const { data } = await api.get(`/wines?${p}`);
            setProducerWines(data.items || []);
        } catch {
            setProducerWines([]);
        } finally {
            setLoadingWines(false);
        }
    };

    // ---------- Grid drill-down (Fase 7.4) ---------- //
    // In modalità Griglia il flow è puramente per card: paesi → regioni →
    // province (solo IT) → produttori → vini. La bottom-sheet NON è coinvolta.
    const gridPickCountry = (c) => {
        setGridCountry(c);
        setGridRegion(null);
        setGridProvincia(null);
        setGridProducer(null);
        setGridStep(1);
    };
    const gridPickRegion = (r) => {
        setGridRegion(r);
        setGridProvincia(null);
        setGridProducer(null);
        const isIt = (gridCountry || "").toLowerCase() === "italia";
        if (isIt) {
            setGridStep(2);
        } else {
            // Esteri: salta le province, va ai produttori.
            setGridStep(3);
            fetchProducers({ country: gridCountry, region: r });
        }
    };
    const gridPickProvincia = (p) => {
        setGridProvincia(p);
        setGridProducer(null);
        setGridStep(3);
        fetchProducers({ country: "Italia", region: gridRegion, provincia: p });
    };
    const gridPickProducer = (p) => {
        setGridProducer(p);
        setGridStep(4);
        fetchProducerWines(p);
    };
    const gridBack = () => {
        if (gridStep >= 4) { setGridProducer(null); setGridStep(3); return; }
        if (gridStep === 3) {
            setGridProducer(null);
            // Se paese è IT e c'è provincia → torna a step 2 (province); altrimenti step 1 (regioni)
            const isIt = (gridCountry || "").toLowerCase() === "italia";
            if (isIt && gridProvincia) {
                setGridProvincia(null);
                setGridStep(2);
            } else {
                setGridRegion(null);
                setGridStep(1);
            }
            return;
        }
        if (gridStep === 2) { setGridRegion(null); setGridProvincia(null); setGridStep(1); return; }
        if (gridStep === 1) { setGridCountry(null); setGridStep(0); return; }
    };
    const gridReset = () => {
        setGridStep(0);
        setGridCountry(null); setGridRegion(null); setGridProvincia(null); setGridProducer(null);
    };

    // Map interactions --------------------------------------------------- //
    const pickCountry = (c) => {
        // Fase 7.3: al tap su un paese la tendina si apre subito con l'elenco
        // cantine di quel paese (comportamento identico alla griglia). La
        // mappa passa al livello 1 e fa fit-bounds sul paese.
        setCountry(c);
        setLevel(1);
        setSheetOpen(true);
        setSheetSnap("half");
        fetchProducers({ country: c });
    };
    const pickRegion = (rname) => {
        // Fase 7.3: per l'Italia si può ancora entrare a livello 2 (province).
        // Per i paesi esteri fermarsi qui: la tendina mostra le cantine della
        // regione, la mappa resta al livello 1 con la regione evidenziata.
        const isIt = (country || "").toLowerCase() === "italia";
        setRegion(rname);
        setProvincia(null);
        setLevel(isIt ? 2 : 1);
        setSheetOpen(true);
        setSheetSnap("half");
        fetchProducers({ country: country || "Italia", region: rname });
    };
    const pickProvincia = (pname) => {
        setProvincia(pname);
        setSheetOpen(true);
        setSheetSnap("half");
        fetchProducers({ country: "Italia", region, provincia: pname });
    };
    /* Fase 7.1: quando l'utente clicca una NUOVA regione mentre è già dentro
       il livello province (bottom sheet aperto), riportiamo il livello mappa
       a "1" (regioni) → il pannello si aggiorna sulla nuova regione senza
       reset del percorso. */
    const switchRegion = (rname) => {
        if (!rname || rname === region) return;
        setRegion(rname);
        setProvincia(null);
        setLevel(1);
        setSheetOpen(true);
        setSheetSnap("half");
        fetchProducers({ country: country || "Italia", region: rname });
    };
    const pickDenominazione = (dzone) => {
        // Apre pannello con produttori filtrati per denominazione (best-effort tramite q)
        setSheetOpen(true);
        setSheetSnap("half");
        setLoadingProducers(true);
        setSelectedProducer(null);
        setProducerWines([]);
        api.get("/wines", { params: { limit: 100, q: dzone.name || dzone.region } })
            .then(({ data }) => {
                const agg = {};
                for (const w of data.items || []) {
                    if (!w.producer || w.producer === "—") continue;
                    agg[w.producer] = agg[w.producer]
                        ? { ...agg[w.producer], wine_count: agg[w.producer].wine_count + 1 }
                        : { producer: w.producer, wine_count: 1, provincia: w.provincia };
                }
                setProducers(Object.values(agg).sort((a, b) => b.wine_count - a.wine_count));
            })
            .catch(() => setProducers([]))
            .finally(() => setLoadingProducers(false));
    };

    // Breadcrumb back navigation
    const backToRegions = () => {
        // Torna al livello mappa "regioni" e chiude il pannello.
        setLevel(1);
        setRegion(null);
        setProvincia(null);
        setSelectedProducer(null);
        setProducerWines([]);
        setSheetOpen(false);
    };
    const backToProducers = () => {
        // Torna alla lista produttori senza cambiare il livello mappa.
        setSelectedProducer(null);
        setProducerWines([]);
    };
    const backToCountries = () => {
        setLevel(0);
        setCountry(null);
        setRegion(null);
        setProvincia(null);
        setSheetOpen(false);
    };

    // Header back button (livello 1+): torna al livello precedente della mappa
    const backOneLevel = () => {
        if (selectedProducer) return backToProducers();
        if (provincia) {
            setProvincia(null);
            // Ricarica produttori a livello regione
            if (region) fetchProducers({ country: country || "Italia", region });
            return;
        }
        if (region) return backToRegions();
        if (country) return backToCountries();
    };

    // ---------- Renderers ---------- //
    // Fase 7.2: mappa mondo vettoriale (livello 0, view=map).
    // Nessun TileLayer: sfondo crema + solo confini nazionali GeoJSON.
    const renderWorldMap = () => {
        const activeCountries = new Set(countriesWithWines.map((c) => c.country.toLowerCase()));
        const countByCountry = Object.fromEntries(
            countriesWithWines.map((c) => [c.country.toLowerCase(), c.count])
        );
        return (
            <MapContainer
                center={[35, 15]}
                zoom={2}
                minZoom={2}
                maxZoom={5}
                worldCopyJump={false}
                style={{
                    height: isMobile ? "62vh" : "70vh",
                    minHeight: 400,
                    width: "100%",
                    background: "#F5EFE7",
                    touchAction: isMobile ? "manipulation" : "auto",
                }}
                scrollWheelZoom={!isMobile}
                dragging={!isMobile}
                touchZoom={!isMobile}
                doubleClickZoom={!isMobile}
                zoomControl={!isMobile}
                boxZoom={!isMobile}
                keyboard={!isMobile}
                tap={!isMobile}
                attributionControl={false}
                inertia={!isMobile}
                data-testid="map-world-container"
            >
                <MapStaticEnforcer isMobile={isMobile} />
                <MapAutoInvalidate />
                <GeoJSON
                    key={`world-${countriesWithWines.length}`}
                    data={worldCountriesGeoJson}
                    style={(feat) => {
                        const nameIt = (feat.properties?.name_it || "").toLowerCase();
                        const isActive = activeCountries.has(nameIt);
                        return {
                            color: isActive ? "#8B0033" : "#B8B0A5",
                            weight: isActive ? 1.2 : 0.5,
                            fillColor: isActive ? "#8B0033" : "#E8E1D4",
                            fillOpacity: isActive ? 0.35 : 0.4,
                            interactive: isActive,
                        };
                    }}
                    onEachFeature={(feat, layer) => {
                        const nameIt = feat.properties?.name_it || feat.properties?.name_en;
                        const isActive = activeCountries.has((nameIt || "").toLowerCase());
                        const cnt = countByCountry[(nameIt || "").toLowerCase()] || 0;
                        if (isActive) {
                            layer.bindTooltip(
                                `<b>${nameIt}</b><br/>${cnt} vino${cnt === 1 ? "" : "i"} in carta`,
                                { direction: "center", sticky: true }
                            );
                            layer.on("click", () => pickCountry(nameIt));
                            layer.on("mouseover", (e) => e.target.setStyle({ fillOpacity: 0.55, weight: 1.8 }));
                            layer.on("mouseout", (e) => e.target.setStyle({ fillOpacity: 0.35, weight: 1.2 }));
                        }
                    }}
                />
            </MapContainer>
        );
    };

    const renderCountryPicker = () => (
        <div className="co-container py-8" data-testid="map-country-picker">
            <span className="co-caption block mb-2">Livello 1 · Paese</span>
            <h2 className="text-2xl mb-6">Da dove parti?</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                {countriesWithWines.map((c) => (
                    <button
                        key={c.country}
                        onClick={() => gridPickCountry(c.country)}
                        className="bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] rounded-sm p-4 text-left transition-colors group"
                        data-testid={`map-country-${c.country}`}
                    >
                        <div className="text-lg group-hover:text-[hsl(var(--primary))]">{c.country}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))]">{c.count} vini</div>
                    </button>
                ))}
            </div>
        </div>
    );

    // Fase 7.4/7.7: drill-down griglia — livello regioni (di gridCountry).
    // Fonte: `regionsMeta` (aggregato reale da /wines/regions), non
    // `zones` (map_zones). Motivo: le regioni con vini ma senza poligono
    // disegnato (es. Lisboa, Ribera del Duero, Vallese) DEVONO comparire
    // in griglia — garantisce che nessun vino sia irraggiungibile.
    const renderGridRegions = () => {
        const norm = (s) => (s || "").toLowerCase()
            .normalize("NFD").replace(/\p{Diacritic}/gu, "").trim();
        const countryLc = norm(gridCountry);
        // Aggrego per region (raggruppa duplicati Alsazia/Alsace, Bourgogne/Borgogna).
        // Escludo la "region" che coincide col paese (rumore semantico: se un
        // vino ha region=Portogallo, non è una vera zona).
        const bucket = new Map();
        for (const r of regionsMeta) {
            if (norm(r.country) !== countryLc) continue;
            if (!r.region) continue;
            if (norm(r.region) === countryLc) continue;
            const key = r.region.trim();
            const prev = bucket.get(key) || { region: key, wine_count: 0 };
            prev.wine_count += (r.count || 0);
            bucket.set(key, prev);
        }
        const items = Array.from(bucket.values())
            .filter((r) => (r.wine_count || 0) > 0)
            .sort((a, b) => (b.wine_count || 0) - (a.wine_count || 0));
        return (
            <div className="co-container py-8" data-testid="grid-regions-view">
                <span className="co-caption block mb-2">Livello 2 · {gridCountry} · Regioni</span>
                <h2 className="text-2xl mb-6">Scegli una zona</h2>
                {items.length === 0 ? (
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">Nessuna regione configurata per {gridCountry}.</p>
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                        {items.map((r) => (
                            <button
                                key={r.region}
                                onClick={() => gridPickRegion(r.region)}
                                className="bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] rounded-sm p-4 text-left transition-colors group"
                                data-testid={`grid-region-${r.region}`}
                            >
                                <div className="text-base sm:text-lg group-hover:text-[hsl(var(--primary))] leading-tight">{r.region}</div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{r.wine_count} vini</div>
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Fase 7.4: drill-down griglia — livello province (solo IT).
    // Fonte: `regionsMeta` (aggregato reale da /wines/regions), non
    // `provinceZones` (che ha solo i confini geografici, non i conteggi).
    const renderGridProvinces = () => {
        const regionLc = (gridRegion || "").toLowerCase();
        const norm = (s) => (s || "").toLowerCase()
            .normalize("NFD").replace(/\p{Diacritic}/gu, "").trim();
        // 1. Aggrego province con vini per la regione corrente.
        const withProv = new Map(); // nome provincia → count
        let unassignedCount = 0;
        for (const r of regionsMeta) {
            if ((r.country || "").toLowerCase() !== "italia") continue;
            if (norm(r.region) !== norm(gridRegion)) continue;
            if (r.provincia) {
                const key = r.provincia.trim();
                withProv.set(key, (withProv.get(key) || 0) + (r.count || 0));
            } else {
                unassignedCount += r.count || 0;
            }
        }
        const items = Array.from(withProv.entries())
            .map(([provincia, count]) => ({ provincia, count }))
            .sort((a, b) => b.count - a.count);
        const totalWithProv = items.reduce((s, x) => s + x.count, 0);
        const totalReg = totalWithProv + unassignedCount;
        return (
            <div className="co-container py-8" data-testid="grid-provinces-view">
                <span className="co-caption block mb-2">Livello 3 · {gridRegion} · Province</span>
                <h2 className="text-2xl mb-6">Scegli una provincia</h2>
                {totalReg === 0 ? (
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">
                        Nessun vino trovato in questa regione.
                    </p>
                ) : (
                    <>
                        {items.length === 0 && unassignedCount > 0 && (
                            <p className="text-sm text-[hsl(var(--muted-foreground))] mb-4">
                                Nessuna provincia specificata per i vini di {gridRegion}. Prosegui direttamente ai produttori.
                            </p>
                        )}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {items.map((p) => (
                                <button
                                    key={p.provincia}
                                    onClick={() => gridPickProvincia(p.provincia)}
                                    className="bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] rounded-sm p-4 text-left transition-colors group"
                                    data-testid={`grid-provincia-${p.provincia}`}
                                >
                                    <div className="text-base sm:text-lg group-hover:text-[hsl(var(--primary))] leading-tight">{p.provincia}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{p.count} vini</div>
                                </button>
                            ))}
                            {unassignedCount > 0 && (
                                <button
                                    onClick={() => {
                                        // Vai direttamente ai produttori di TUTTA la regione (senza filtro provincia).
                                        setGridProvincia(null);
                                        setGridStep(3);
                                        fetchProducers({ country: "Italia", region: gridRegion });
                                    }}
                                    className="bg-[hsl(var(--secondary))] border border-dashed border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] rounded-sm p-4 text-left transition-colors group"
                                    data-testid="grid-unassigned-province-btn"
                                >
                                    <div className="text-base sm:text-lg group-hover:text-[hsl(var(--primary))] leading-tight italic">Altre zone di {gridRegion}</div>
                                    <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{unassignedCount} vini · provincia non specificata</div>
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        );
    };

    // Fase 7.4/7.5: drill-down griglia — livello produttori con vini
    // auto-expanded sotto ciascun produttore. Le prime N sezioni caricano
    // subito i vini; le successive usano IntersectionObserver per lazy-load
    // (mantiene le prestazioni con 50+ produttori).
    const renderGridProducers = () => {
        const scopeLabel = gridProvincia || gridRegion || gridCountry || "";
        return (
            <div className="co-container py-8" data-testid="grid-producers-view">
                <span className="co-caption block mb-2">Produttori · {scopeLabel}</span>
                <h2 className="text-2xl mb-6">Le cantine di {scopeLabel}</h2>
                {loadingProducers ? (
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">Caricamento…</p>
                ) : producers.length === 0 ? (
                    <p className="text-sm text-[hsl(var(--muted-foreground))]">Nessun produttore trovato.</p>
                ) : (
                    <div className="space-y-8">
                        {producers.map((p, idx) => (
                            <ProducerSection
                                key={p.producer}
                                producer={p}
                                scope={{ country: gridCountry, region: gridRegion, provincia: gridProvincia }}
                                eagerLoad={idx < 3}
                                onOpenWine={(id) => nav(`/carta?wine=${id}`)}
                                onOpenProducer={() => gridPickProducer(p.producer)}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Fase 7.4: drill-down griglia — livello vini di un produttore.
    const renderGridProducerWines = () => (
        <div className="co-container py-8" data-testid="grid-wines-view">
            <span className="co-caption block mb-2">Vini · {gridProducer}</span>
            <h2 className="text-2xl mb-6">Le etichette di {gridProducer}</h2>
            {loadingWines ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Caricamento…</p>
            ) : producerWines.length === 0 ? (
                <p className="text-sm text-[hsl(var(--muted-foreground))]">Nessun vino trovato.</p>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 mb-6">
                        {producerWines.map((w) => (
                            <button
                                key={w.id}
                                onClick={() => nav(`/carta?wine=${w.id}`)}
                                className="bg-white border border-[hsl(var(--border))] hover:border-[hsl(var(--primary))] rounded-sm p-4 text-left transition-colors group"
                                data-testid={`grid-wine-${w.id}`}
                            >
                                <div className="text-xs text-[hsl(var(--muted-foreground))] uppercase tracking-wide mb-1">{w.type || "vino"}</div>
                                <div className="text-base group-hover:text-[hsl(var(--primary))] leading-tight">
                                    {w.name}
                                    {w.vintage && <span className="ml-1 italic text-[hsl(var(--muted-foreground))] text-sm">{w.vintage}</span>}
                                </div>
                                <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{w.denomination || w.region || ""}</div>
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => {
                            const p = new URLSearchParams();
                            p.set("produttore", gridProducer);
                            if (gridProvincia) p.set("provincia", gridProvincia);
                            else if (gridRegion) p.set("regione", gridRegion);
                            if (gridCountry) p.set("paese", gridCountry);
                            nav(`/carta?${p}`);
                        }}
                        className="co-btn-primary"
                        data-testid="grid-see-in-catalog-btn"
                    >
                        Vedi tutti i vini di {gridProducer} nel catalogo
                    </button>
                </>
            )}
        </div>
    );

    // Fase 7.4: breadcrumb per la vista Griglia.
    const renderGridBreadcrumb = () => {
        if (gridStep === 0) return null;
        const crumbs = [];
        crumbs.push({ label: "Paesi", onClick: () => gridReset() });
        if (gridCountry) crumbs.push({ label: gridCountry, onClick: gridStep > 1 ? () => { setGridStep(1); setGridRegion(null); setGridProvincia(null); setGridProducer(null); } : null });
        if (gridRegion) crumbs.push({ label: gridRegion, onClick: gridStep > 2 ? () => { const isIt=(gridCountry||"").toLowerCase()==="italia"; if(isIt){setGridStep(2); setGridProvincia(null); setGridProducer(null);} } : null });
        if (gridProvincia) crumbs.push({ label: gridProvincia, onClick: gridStep > 3 ? () => { setGridStep(3); setGridProducer(null); fetchProducers({ country: "Italia", region: gridRegion, provincia: gridProvincia }); } : null });
        if (gridProducer) crumbs.push({ label: gridProducer, onClick: null });
        return (
            <div className="co-container flex items-center flex-wrap gap-x-1.5 gap-y-1 text-xs sm:text-sm py-2" data-testid="grid-breadcrumb">
                <button onClick={gridBack} className="inline-flex items-center gap-1 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--primary))]" data-testid="grid-back-btn">
                    <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Indietro
                </button>
                <span className="text-[hsl(var(--muted-foreground))] mx-2">·</span>
                {crumbs.map((cr, i) => (
                    <span key={i} className="inline-flex items-center gap-1.5">
                        {i > 0 && <ChevronRight className="w-3 h-3 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />}
                        {cr.onClick ? (
                            <button onClick={cr.onClick} className="text-[hsl(var(--primary))] hover:underline underline-offset-2">
                                {cr.label}
                            </button>
                        ) : (
                            <span className="text-[hsl(var(--foreground))]">{cr.label}</span>
                        )}
                    </span>
                ))}
            </div>
        );
    };

    const renderRegionMap = () => {
        const isIT = (country || "").toLowerCase() === "italia";
        if (isIT) {
            // Fase 8.4 · usa la lista filtrata per evitare bbox-paese rettangolari.
            const featColl = zonesToFeatureCollection(renderableCountryZones);
            return (
                <MapContainer
                    center={[42.5, 12.5]}
                    zoom={5.5}
                    minZoom={3} maxZoom={9}
                    style={{ height: isMobile ? "60vh" : "70vh", minHeight: 380, width: "100%", background: "#F5EFE7", touchAction: isMobile ? "manipulation" : "auto" }}
                    scrollWheelZoom={!isMobile}
                    dragging={!isMobile}
                    touchZoom={!isMobile}
                    doubleClickZoom={!isMobile}
                    zoomControl={!isMobile}
                    boxZoom={!isMobile}
                    keyboard={!isMobile}
                    tap={!isMobile}
                    attributionControl={false}
                    inertia={!isMobile}>
                    <MapStaticEnforcer isMobile={isMobile} />
                    <MapAutoInvalidate />
                    <FitBoundsToGeoJson data={featColl} deps={[country, zones.length]} padding={[30, 30]} />
                    <GeoJSON
                        key={`regions-IT-${zones.length}`}
                        data={featColl}
                        style={(feat) => {
                            const cnt = feat.properties?.count || 0;
                            const isActive = region && (feat.properties?.region || "").toLowerCase() === region.toLowerCase();
                            return {
                                color: isActive ? denomStroke : (cnt > 0 ? activeStroke : inactiveColor),
                                weight: isActive ? 3 : (cnt > 0 ? 2 : 1),
                                fillColor: cnt > 0 ? activeColor : inactiveColor,
                                fillOpacity: cnt > 0 ? Math.min(0.65, 0.15 + cnt / 60) : 0.05,
                            };
                        }}
                        onEachFeature={(feat, layer) => {
                            const rn = feat.properties?.region;
                            const cnt = feat.properties?.count || 0;
                            layer.bindTooltip(`<b>${feat.properties?.name || rn}</b><br/>${cnt ? cnt + " vini" : "non in carta"}`,
                                { direction: "center", sticky: true });
                            if (cnt > 0) layer.on("click", () => pickRegion(rn));
                        }}
                    />
                    {denomEnabled && currentDenominazioni.map((d) => {
                        const pos = zonePolygonPositions(d);
                        if (!pos && !d.geojson) return null;
                        return pos ? (
                            <Polygon key={`denom-${d.id}`} positions={pos}
                                pathOptions={{
                                    color: denomStroke, weight: 1.5,
                                    dashArray: "4 3", fillColor: activeColor, fillOpacity: 0.12,
                                }}
                                eventHandlers={{ click: () => pickDenominazione(d) }}
                            >
                                <Tooltip direction="center" opacity={0.9}>
                                    <div className="text-xs"><b>{d.name}</b><br />{d.wine_count} vini · denominazione</div>
                                </Tooltip>
                            </Polygon>
                        ) : null;
                    })}
                </MapContainer>
            );
        }
        /* Fase 7.3 — paesi esteri: SOLO confini reali admin-1 da Natural Earth,
           risolti attraverso `aliasAdmin1` per matchare i nomi vinicoli italiani
           dell'anagrafica (es. "Champagne" → "Grand Est"). Niente più poligoni
           AI "a scatola". Se un nome regione dell'anagrafica NON risolve a
           nessuna feature admin-1, quella regione NON viene renderizzata
           (compare comunque nella tendina laterale). */
        const norm = (s) => (s || "").toLowerCase()
            .normalize("NFD").replace(/\p{Diacritic}/gu, "").trim();
        const countryLc = norm(country);
        // Estraggo la country_en corrispondente dal worldCountries dataset
        const countryFeat = worldCountriesGeoJson.features.find(
            (f) => norm(f.properties.name_it) === countryLc || norm(f.properties.name_en) === countryLc
        );
        const countryEn = countryFeat?.properties?.name_en || country;
        // Lookup: nome regione admin-1 normalizzato → count vini
        const admin1Count = {};
        for (const z of currentCountryZones) {
            const cnt = z.wine_count || 0;
            const targets = aliasAdmin1(z.region, countryEn);
            for (const t of targets) {
                const key = norm(t);
                admin1Count[key] = (admin1Count[key] || 0) + cnt;
            }
        }
        // Filtro le feature admin-1 per paese + presenza nell'alias mapping.
        const feats = worldSubdivisionsGeoJson.features.filter((f) => {
            const cIt = norm(f.properties.country_it);
            const cEn = norm(f.properties.country_en);
            if (cIt !== countryLc && cEn !== countryLc) return false;
            return norm(f.properties.region) in admin1Count;
        });
        // Se ho count>0, uso quello; altrimenti conteggio anche zone senza wine_count valorizzato
        const enrichedFeatColl = {
            type: "FeatureCollection",
            features: feats.map((f) => ({
                ...f,
                properties: {
                    ...f.properties,
                    count: admin1Count[norm(f.properties.region)] || 0,
                },
            })),
        };
        // Feature del solo paese per il country outline + il fit-bounds.
        const countryOnlyFeatColl = countryFeat
            ? { type: "FeatureCollection", features: [countryFeat] }
            : null;
        return (
            <MapContainer
                center={[47, 3]}
                zoom={4}
                minZoom={2}
                maxZoom={8}
                style={{ height: isMobile ? "60vh" : "70vh", minHeight: 380, width: "100%", background: "#F5EFE7", touchAction: isMobile ? "manipulation" : "auto" }}
                scrollWheelZoom={!isMobile}
                dragging={!isMobile}
                touchZoom={!isMobile}
                doubleClickZoom={!isMobile}
                zoomControl={!isMobile}
                boxZoom={!isMobile}
                keyboard={!isMobile}
                tap={!isMobile}
                attributionControl={false}
                inertia={!isMobile}>
                <MapStaticEnforcer isMobile={isMobile} />
                <MapAutoInvalidate />
                {/* Fit-bounds sempre sul paese (Fase 7.3). Se ci sono regioni
                    enriched le zoomiamo dentro, altrimenti restiamo sul paese. */}
                <FitBoundsToGeoJson
                    data={enrichedFeatColl.features.length > 0 ? enrichedFeatColl : countryOnlyFeatColl}
                    deps={[country, enrichedFeatColl.features.length]}
                    padding={[40, 40]}
                />
                {/* Outline paese (sotto) per contesto geografico */}
                {countryOnlyFeatColl && (
                    <GeoJSON
                        key={`country-outline-${country}`}
                        data={countryOnlyFeatColl}
                        style={() => ({
                            color: "#B8B0A5",
                            weight: 1,
                            fillColor: "#E8E1D4",
                            fillOpacity: 0.35,
                            interactive: false,
                        })}
                    />
                )}
                {enrichedFeatColl.features.length > 0 && (
                    <GeoJSON
                        key={`regions-FOR-${country}-${feats.length}`}
                        data={enrichedFeatColl}
                        style={(feat) => {
                            const cnt = feat.properties.count || 0;
                            const isActive = region && (
                                aliasAdmin1(region, countryEn).some(a => norm(a) === norm(feat.properties.region))
                            );
                            return {
                                color: isActive ? denomStroke : activeStroke,
                                weight: isActive ? 3 : 2,
                                fillColor: activeColor,
                                fillOpacity: Math.min(0.65, 0.25 + cnt / 30),
                            };
                        }}
                        onEachFeature={(feat, layer) => {
                            const admin1Name = feat.properties.region;
                            // Trovo la zona vinicola che ha risolto a questa admin1 (per il click)
                            const zoneMatch = currentCountryZones.find(z =>
                                aliasAdmin1(z.region, countryEn).some(a => norm(a) === norm(admin1Name))
                            );
                            const zoneName = zoneMatch?.region || admin1Name;
                            const cnt = feat.properties.count || 0;
                            layer.bindTooltip(
                                `<b>${zoneName}</b><br/>${cnt} vino${cnt === 1 ? "" : "i"}`,
                                { direction: "center", sticky: true }
                            );
                            layer.on("click", () => pickRegion(zoneName));
                        }}
                    />
                )}
            </MapContainer>
        );
    };

    const renderProvinceMap = () => {
        if (!region) return null;
        const provinces = currentRegionProvinces;
        const featColl = zonesToFeatureCollection(provinces);
        // Fase 7.1: overlay regioni Italia (esclusa quella corrente).
        // Consente il "cambio zona diretto" senza dover premere Indietro:
        // click su un'altra regione → switchRegion() aggiorna il pannello
        // e la mappa scende al livello 1.
        const otherRegions = zones.filter((z) =>
            (z.country || "").toLowerCase() === "italia" &&
            (z.region || "").toLowerCase() !== region.toLowerCase() &&
            (z.wine_count || 0) > 0 &&
            isRenderableZone(z)  // Fase 8.4 · esclude bbox-paese
        );
        const otherRegionsFeatColl = zonesToFeatureCollection(otherRegions);
        return (
            <MapContainer center={[42.5, 12.5]} zoom={6} minZoom={5} maxZoom={10}
                style={{ height: isMobile ? "60vh" : "70vh", minHeight: 380, width: "100%", background: "#F5EFE7", touchAction: isMobile ? "manipulation" : "auto" }}
                scrollWheelZoom={!isMobile}
                dragging={!isMobile}
                touchZoom={!isMobile}
                doubleClickZoom={!isMobile}
                zoomControl={!isMobile}
                boxZoom={!isMobile}
                keyboard={!isMobile}
                tap={!isMobile}
                attributionControl={false}
                inertia={!isMobile}>
                <MapStaticEnforcer isMobile={isMobile} />
                <MapAutoInvalidate />
                {/* Fase 7.2: vettoriale — nessun TileLayer. */}
                {/* Regioni "vicine" cliccabili per cambio zona diretto */}
                <GeoJSON
                    key={`other-regions-${region}`}
                    data={otherRegionsFeatColl}
                    style={() => ({
                        color: "#8B7B6E",
                        weight: 1,
                        fillColor: "#B8B0A5",
                        fillOpacity: 0.15,
                        dashArray: "2 3",
                    })}
                    onEachFeature={(feat, layer) => {
                        const rn = feat.properties?.region;
                        const cnt = feat.properties?.count || 0;
                        layer.bindTooltip(`<b>${feat.properties?.name || rn}</b><br/>${cnt} vini · tocca per passare a questa regione`,
                            { direction: "center", sticky: true });
                        layer.on("click", () => switchRegion(rn));
                    }}
                />
                <GeoJSON
                    key={`prov-${region}`}
                    data={featColl}
                    style={(feat) => {
                        const cnt = feat.properties?.count || 0;
                        const isActive = provincia && (feat.properties?.region || "").toLowerCase() === provincia.toLowerCase();
                        return {
                            color: isActive ? denomStroke : (cnt > 0 ? activeStroke : inactiveColor),
                            weight: isActive ? 3 : (cnt > 0 ? 2 : 1),
                            fillColor: cnt > 0 ? activeColor : inactiveColor,
                            fillOpacity: cnt > 0 ? Math.min(0.65, 0.2 + cnt / 30) : 0.05,
                        };
                    }}
                    onEachFeature={(feat, layer) => {
                        const pn = feat.properties?.region;
                        const cnt = feat.properties?.count || 0;
                        layer.bindTooltip(`<b>${feat.properties?.name || pn}</b><br/>${cnt ? cnt + " produttori" : "nessun produttore"}`,
                            { direction: "center", sticky: true });
                        if (cnt > 0) layer.on("click", () => pickProvincia(pn));
                    }}
                />
                {denomEnabled && currentDenominazioni.map((d) => {
                    const pos = zonePolygonPositions(d);
                    if (!pos) return null;
                    return (
                        <Polygon key={`denom-p-${d.id}`} positions={pos}
                            pathOptions={{
                                color: denomStroke, weight: 1.5,
                                dashArray: "4 3", fillColor: activeColor, fillOpacity: 0.12,
                            }}
                            eventHandlers={{ click: () => pickDenominazione(d) }}
                        >
                            <Tooltip direction="center" opacity={0.9}>
                                <div className="text-xs"><b>{d.name}</b><br />denominazione</div>
                            </Tooltip>
                        </Polygon>
                    );
                })}
            </MapContainer>
        );
    };

    const titleFor = () => {
        if (viewMode === "grid") {
            if (gridStep === 0) return "La mappa della carta";
            if (gridStep === 4) return gridProducer;
            if (gridStep === 3) return `Cantine · ${gridProvincia || gridRegion || gridCountry}`;
            if (gridStep === 2) return `${gridRegion} · Province`;
            if (gridStep === 1) return `${gridCountry} · Zone`;
        }
        if (level === 0) return "La mappa della carta";
        if (level === 1 && country === "Italia") return "Italia · Regioni";
        if (level === 1) return `${country} · Zone`;
        if (level >= 2) return `${region} · Province`;
        return "Territorio";
    };

    // Fase 7.4: toggle Griglia|Mappa sempre visibile (in entrambe modalità
    // e a tutti i livelli). Il cambio modalità resetta il drill-down grid o
    // il livello mappa in modo intuitivo.
    const switchViewMode = (m) => {
        setViewMode(m);
        if (m === "grid") {
            // Se ero già in griglia, non fare niente. Se venivo dalla mappa,
            // resettiamo il drill grid a paesi per pulizia.
            if (viewMode !== "grid") gridReset();
            // Chiudi sempre la sheet.
            setSheetOpen(false);
        } else {
            // Passo in mappa: chiudo eventuali stati grid e resetto la mappa a livello 0.
            setLevel(0);
            setCountry(null); setRegion(null); setProvincia(null);
            setSheetOpen(false);
            setSelectedProducer(null); setProducerWines([]);
        }
    };

    return (
        <main className="min-h-screen bg-[hsl(var(--background))]" data-testid="map-explore-page">
            <div className="co-container pt-8 pb-4 flex items-start gap-3">
                {viewMode === "map" && level > 0 && (
                    <button onClick={backOneLevel} className="co-btn-ghost" data-testid="map-back-btn">
                        <ChevronLeft className="w-4 h-4" /> Indietro
                    </button>
                )}
                <div className="flex-1">
                    <span className="co-caption block mb-1">Esplora per Territorio</span>
                    <h1 className="text-3xl sm:text-4xl">{titleFor()}</h1>
                </div>
                {/* Fase 7.4: toggle "Griglia | Mappa" — sempre visibile,
                    pill grande e prominente (stile analogo al toggle di /carta). */}
                <div
                    className="inline-flex border border-[hsl(var(--border))] rounded-sm overflow-hidden bg-white shrink-0 self-center shadow-sm"
                    data-testid="map-view-toggle"
                    role="tablist"
                    aria-label="Modalità di visualizzazione"
                >
                    <button
                        type="button"
                        onClick={() => switchViewMode("grid")}
                        className={`flex items-center gap-2 px-4 sm:px-5 h-12 text-sm sm:text-base transition-colors ${
                            viewMode === "grid"
                                ? "bg-[hsl(var(--primary))] text-white"
                                : "hover:bg-black/[0.03] text-[hsl(var(--foreground))]"
                        }`}
                        data-testid="map-view-grid-btn"
                        aria-pressed={viewMode === "grid"}
                        title="Griglia paesi"
                    >
                        <LayoutGrid className="w-5 h-5" strokeWidth={1.5} />
                        <span>Griglia</span>
                    </button>
                    <button
                        type="button"
                        onClick={() => switchViewMode("map")}
                        className={`flex items-center gap-2 px-4 sm:px-5 h-12 text-sm sm:text-base transition-colors ${
                            viewMode === "map"
                                ? "bg-[hsl(var(--primary))] text-white"
                                : "hover:bg-black/[0.03] text-[hsl(var(--foreground))]"
                        }`}
                        data-testid="map-view-map-btn"
                        aria-pressed={viewMode === "map"}
                        title="Mappa"
                    >
                        <MapIcon className="w-5 h-5" strokeWidth={1.5} />
                        <span>Mappa</span>
                    </button>
                </div>
            </div>

            {/* Vista Griglia (Fase 7.4): drill-down solo a griglie, no sheet. */}
            {viewMode === "grid" && (
                <>
                    {renderGridBreadcrumb()}
                    {gridStep === 0 && renderCountryPicker()}
                    {gridStep === 1 && renderGridRegions()}
                    {gridStep === 2 && renderGridProvinces()}
                    {gridStep === 3 && renderGridProducers()}
                    {gridStep === 4 && renderGridProducerWines()}
                </>
            )}

            {/* Vista Mappa (comportamento esistente + tendina). */}
            {viewMode === "map" && level === 0 && (
                <div className="w-full" data-testid="map-container">{renderWorldMap()}</div>
            )}
            {viewMode === "map" && level === 1 && (
                <div className="w-full" data-testid="map-container">{renderRegionMap()}</div>
            )}
            {viewMode === "map" && level >= 2 && (
                <div className="w-full" data-testid="map-container">{renderProvinceMap()}</div>
            )}

            {/* Fase 7.5: attribution rimosso su richiesta cliente.
                `attributionControl={false}` sui MapContainer disabilita
                anche il badge Leaflet di default. */}

            {viewMode === "map" && sheetOpen && (
                <MapBottomSheet
                    snap={sheetSnap}
                    setSnap={setSheetSnap}
                    onClose={() => setSheetOpen(false)}
                    country={country}
                    region={region}
                    provincia={provincia}
                    selectedProducer={selectedProducer}
                    loadingProducers={loadingProducers}
                    loadingWines={loadingWines}
                    producers={producers}
                    producerWines={producerWines}
                    onSelectProducer={fetchProducerWines}
                    onBackToProducers={backToProducers}
                    onBackToRegion={() => { setProvincia(null); if (region) fetchProducers({ country: country || "Italia", region }); }}
                    onBackToCountry={backToRegions}
                    onOpenWine={(id) => nav(`/carta?wine=${id}`)}
                    onGoToCatalog={() => {
                        const p = new URLSearchParams();
                        if (selectedProducer) p.set("produttore", selectedProducer);
                        if (provincia) p.set("provincia", provincia);
                        else if (region) p.set("regione", region);
                        if (country) p.set("paese", country);
                        nav(`/carta?${p}`);
                    }}
                />
            )}
        </main>
    );
}

/* ================================================================== */
/*  Bottom sheet                                                       */
/* ================================================================== */

function MapBottomSheet({
    snap, setSnap, onClose,
    country, region, provincia, selectedProducer,
    loadingProducers, loadingWines, producers, producerWines,
    onSelectProducer, onBackToProducers, onBackToRegion, onBackToCountry,
    onOpenWine, onGoToCatalog,
}) {
    // Drag state --------------------------------------------------------- //
    const [dragging, setDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState(0); // px offset while dragging
    const startYRef = useRef(0);
    const startSnapRef = useRef(snap);

    const snapToPx = useCallback((s) => {
        // Ratio of viewport occupied by the sheet (visible portion).
        const vh = typeof window !== "undefined" ? window.innerHeight : 800;
        return Math.round(vh * SNAP[s]);
    }, []);

    const onPointerDown = (e) => {
        setDragging(true);
        setDragOffset(0);
        startYRef.current = e.clientY;
        startSnapRef.current = snap;
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const onPointerMove = (e) => {
        if (!dragging) return;
        // Positive offset = drag verso il basso → riduce l'altezza del pannello
        setDragOffset(e.clientY - startYRef.current);
    };
    const onPointerUp = () => {
        if (!dragging) return;
        setDragging(false);
        const vh = window.innerHeight;
        const baseH = vh * SNAP[startSnapRef.current];
        const finalH = baseH - dragOffset; // in px
        const finalRatio = Math.max(0, Math.min(1, finalH / vh));
        // Choose closest snap OR close if below peek threshold
        if (finalRatio < SNAP.peek * 0.55) {
            onClose();
        } else {
            const candidates = ["peek", "half", "full"];
            const closest = candidates.reduce((a, b) =>
                Math.abs(SNAP[a] - finalRatio) < Math.abs(SNAP[b] - finalRatio) ? a : b
            );
            setSnap(closest);
        }
        setDragOffset(0);
    };

    // Compute final height at render time
    const vh = typeof window !== "undefined" ? window.innerHeight : 800;
    const baseH = snapToPx(snap);
    const heightPx = Math.max(80, Math.min(vh * SNAP.full, baseH - dragOffset));

    // Breadcrumb segments -----------------------------------------------
    const crumbs = [];
    if (country) crumbs.push({ label: country, onClick: onBackToCountry });
    if (region) crumbs.push({ label: region, onClick: onBackToCountry });
    if (provincia) crumbs.push({ label: provincia, onClick: selectedProducer ? onBackToProducers : onBackToRegion });
    if (selectedProducer) crumbs.push({ label: selectedProducer, onClick: null });

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[500] sm:right-6 sm:left-auto sm:bottom-6 sm:w-[440px] pointer-events-none"
            data-testid="map-area-sheet-container"
        >
            <div
                className="pointer-events-auto bg-white border border-[hsl(var(--border))] shadow-2xl rounded-t-2xl sm:rounded-2xl flex flex-col mx-auto sm:mx-0 w-full"
                style={{
                    height: `${heightPx}px`,
                    transition: dragging ? "none" : "height 220ms ease",
                    willChange: "height",
                }}
                data-testid="map-area-sheet"
            >
                {/* Drag handle */}
                <div
                    className="pt-2 pb-1 cursor-grab active:cursor-grabbing touch-none select-none"
                    onPointerDown={onPointerDown}
                    onPointerMove={onPointerMove}
                    onPointerUp={onPointerUp}
                    onPointerCancel={onPointerUp}
                    data-testid="map-sheet-handle"
                >
                    <div className="mx-auto w-10 h-1 rounded-full bg-[hsl(var(--muted-foreground))]/40" />
                </div>

                {/* Header: breadcrumb + close */}
                <div className="flex items-center justify-between gap-2 px-4 pb-2 border-b border-[hsl(var(--border))]">
                    <div className="min-w-0 flex-1">
                        <div className="co-caption text-[10px] mb-0.5">
                            {selectedProducer ? "Produttore" : (provincia ? "Provincia" : (region ? "Regione" : "Zona"))}
                        </div>
                        <div className="flex items-center flex-wrap gap-x-1.5 gap-y-0.5 text-sm" data-testid="map-sheet-breadcrumb">
                            {crumbs.map((c, i) => {
                                const isLast = i === crumbs.length - 1;
                                return (
                                    <span key={i} className="flex items-center gap-1.5">
                                        {c.onClick && !isLast ? (
                                            <button
                                                onClick={c.onClick}
                                                className="hover:text-[hsl(var(--primary))] transition-colors"
                                                data-testid={`map-sheet-crumb-${i}`}
                                            >
                                                {c.label}
                                            </button>
                                        ) : (
                                            <span className={isLast ? "font-medium text-[hsl(var(--foreground))]" : "text-[hsl(var(--muted-foreground))]"} data-testid={`map-sheet-crumb-${i}`}>
                                                {c.label}
                                            </span>
                                        )}
                                        {!isLast && <ChevronRight className="w-3 h-3 text-[hsl(var(--muted-foreground))]" strokeWidth={1.5} />}
                                    </span>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex items-center gap-1">
                        {selectedProducer && (
                            <button onClick={onBackToProducers} className="p-1.5 hover:bg-[hsl(var(--secondary))] rounded-sm" data-testid="map-sheet-back" title="Torna ai produttori">
                                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        )}
                        <button onClick={onClose} className="p-1.5 hover:bg-[hsl(var(--secondary))] rounded-sm" data-testid="map-sheet-close" title="Chiudi">
                            <X className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                    </div>
                </div>

                {/* Content --------------------------------------------- */}
                <div className="flex-1 overflow-y-auto overscroll-contain">
                    {!selectedProducer ? (
                        <ProducersList
                            loading={loadingProducers}
                            producers={producers}
                            onSelect={onSelectProducer}
                            showRegionGroups={!!country && !region}
                        />
                    ) : (
                        <WinesList
                            loading={loadingWines}
                            wines={producerWines}
                            onOpen={onOpenWine}
                        />
                    )}
                </div>

                {/* Footer CTA */}
                <div className="p-3 border-t border-[hsl(var(--border))]">
                    <button
                        onClick={onGoToCatalog}
                        className="co-btn-primary w-full justify-center text-sm"
                        data-testid="map-goto-catalog"
                    >
                        {selectedProducer
                            ? `Vedi tutti i vini di ${selectedProducer} in catalogo`
                            : `Vedi tutti in catalogo${provincia ? ` (${provincia})` : region ? ` (${region})` : ""}`}
                    </button>
                </div>
            </div>
        </div>
    );
}

function ProducersList({ loading, producers, onSelect, showRegionGroups }) {
    if (loading) {
        return <div className="p-4 text-sm text-[hsl(var(--muted-foreground))]" data-testid="map-sheet-loading">Caricamento…</div>;
    }
    if (!producers.length) {
        return <div className="p-4 italic text-sm text-[hsl(var(--muted-foreground))]" data-testid="map-sheet-empty-producers">Nessun produttore in questa zona.</div>;
    }
    // Fase 7.7: se siamo al livello country (senza region), raggruppa per
    // region per garantire che ANCHE le zone senza poligono disegnato
    // (Lisboa, Ribera del Duero, Cantone Vallese, ecc.) siano visibili
    // come intestazioni con i loro produttori — nessun vino irraggiungibile.
    if (showRegionGroups) {
        const groups = new Map();
        for (const p of producers) {
            const key = (p.region || "—").trim();
            if (!groups.has(key)) groups.set(key, []);
            groups.get(key).push(p);
        }
        const ordered = Array.from(groups.entries())
            .sort((a, b) => {
                const totalDiff = b[1].reduce((s, x) => s + (x.wine_count || 0), 0) -
                                  a[1].reduce((s, x) => s + (x.wine_count || 0), 0);
                if (totalDiff !== 0) return totalDiff;
                return a[0].localeCompare(b[0]);
            });
        return (
            <div data-testid="map-sheet-producers-list">
                {ordered.map(([regionName, list]) => {
                    const total = list.reduce((s, x) => s + (x.wine_count || 0), 0);
                    return (
                        <div key={regionName} className="border-t border-[hsl(var(--border))] first:border-t-0">
                            <div className="px-4 py-2 bg-[hsl(var(--secondary))]/50 flex items-baseline justify-between"
                                 data-testid={`map-sheet-region-group-${regionName}`}>
                                <span className="text-sm font-medium">{regionName}</span>
                                <span className="text-[11px] text-[hsl(var(--muted-foreground))]">
                                    {list.length} cantin{list.length === 1 ? "a" : "e"} · {total} vini
                                </span>
                            </div>
                            <div className="divide-y divide-[hsl(var(--border))]">
                                {list.map((p) => (
                                    <button
                                        key={p.producer}
                                        onClick={() => onSelect(p.producer)}
                                        className="w-full text-left px-4 py-3 hover:bg-[hsl(var(--secondary))] transition-colors flex items-center gap-3"
                                        data-testid={`map-producer-${p.producer}`}
                                    >
                                        <div className="flex-1 min-w-0">
                                            <div className="font-medium truncate">{p.producer}</div>
                                            {p.provincia && (
                                                <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{p.provincia}</div>
                                            )}
                                        </div>
                                        <span className="shrink-0 text-xs px-2 py-0.5 border border-[hsl(var(--border))] rounded-full text-[hsl(var(--muted-foreground))]" data-testid={`map-producer-count-${p.producer}`}>
                                            {p.wine_count} vin{p.wine_count === 1 ? "o" : "i"}
                                        </span>
                                        <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" strokeWidth={1.5} />
                                    </button>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    }
    return (
        <div className="divide-y divide-[hsl(var(--border))]" data-testid="map-sheet-producers-list">
            {producers.map((p) => (
                <button
                    key={p.producer}
                    onClick={() => onSelect(p.producer)}
                    className="w-full text-left px-4 py-3 hover:bg-[hsl(var(--secondary))] transition-colors flex items-center gap-3"
                    data-testid={`map-producer-${p.producer}`}
                >
                    <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.producer}</div>
                        {p.provincia && (
                            <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">{p.provincia}</div>
                        )}
                    </div>
                    <span className="shrink-0 text-xs px-2 py-0.5 border border-[hsl(var(--border))] rounded-full text-[hsl(var(--muted-foreground))]" data-testid={`map-producer-count-${p.producer}`}>
                        {p.wine_count} vin{p.wine_count === 1 ? "o" : "i"}
                    </span>
                    <ChevronRight className="w-4 h-4 text-[hsl(var(--muted-foreground))] shrink-0" strokeWidth={1.5} />
                </button>
            ))}
        </div>
    );
}

function WinesList({ loading, wines, onOpen }) {
    if (loading) {
        return <div className="p-4 text-sm text-[hsl(var(--muted-foreground))]" data-testid="map-sheet-loading">Caricamento…</div>;
    }
    if (!wines.length) {
        return <div className="p-4 italic text-sm text-[hsl(var(--muted-foreground))]" data-testid="map-sheet-empty-wines">Nessun vino di questo produttore.</div>;
    }
    return (
        <div className="divide-y divide-[hsl(var(--border))]" data-testid="map-sheet-wines-list">
            {wines.map((w) => (
                <button
                    key={w.id}
                    onClick={() => onOpen(w.id)}
                    className="w-full text-left px-4 py-3 hover:bg-[hsl(var(--secondary))] transition-colors"
                    data-testid={`map-wine-${w.id}`}
                >
                    <div className="font-medium truncate">
                        {w.name}
                        {w.vintage && <span className="ml-1 italic text-[hsl(var(--muted-foreground))]">{w.vintage}</span>}
                    </div>
                    <div className="text-[11px] text-[hsl(var(--muted-foreground))] truncate">
                        {w.denomination || w.region || w.country || ""}
                    </div>
                </button>
            ))}
        </div>
    );
}
